// rcTransformation.service.ts

import { Injectable } from '@angular/core'; // Importing from Angular core, adjust if not using Angular
import { VideoRotationMessage } from '../messages/xsight';
import { TransformAnimationService } from './transformAnimationService';
import { RcContext } from '../context/rcContext';
import { DeviceContext } from '../context/deviceContext';

@Injectable({
  providedIn: 'root', // Adjust the providedIn value based on your Angular module setup
})
export class RcTransformationService {
    // zoom related
    private MINIMUM_DEVICE_SCALE = 25;  // represents the minimum scale used for PC_RESTART msg
    private MAXIMUM_DEVICE_SCALE = 100; // represents the maximum scale used for PC_RESTART msg
    private currentZoomScale: number = 1.0;
    private previousDeviceScale: number = 0.0;
    private targetDeviceScale: number = 0;

    // rotate related
    private previousRotateDeg: number = 0;
    private currentRotateDeg: number = 0;

    private transformAnimationService: TransformAnimationService;
    private timer: NodeJS.Timeout;

    private outerCanvasWidth: number = 0;
    private outerCanvasHeight: number = 0;

    constructor() {
        this.transformAnimationService = new TransformAnimationService(document.getElementById('canvasOuter'));
    }

    public transform(startScale: number, targetScale: number, needTransition?: boolean) {
        if (this.changeScale(startScale, targetScale)) {
            this.applyTransform(needTransition);
        }
    }

    public getCurrentZoomScale(): number {
        return this.currentZoomScale;
    }

    /**
     * e.g. the auto zoom scale for the current canvas is 36, and target scale is 46
     * it will return 46 / 36
     * @param startScale - the auto zoom scale for the current canvas
     * @param targetScale
     */
    private changeScale(startScale: number, targetScale: number) {
        if (this.isCanvasVisible())  {
            // Logic: CanvasOuter should be reset scale to 1 for tooltip
            this.targetDeviceScale = targetScale;
            this.currentZoomScale = targetScale / startScale;
            this.previousDeviceScale = this.targetDeviceScale;
            return true;
        }
        return false;
    }
    // Add more methods as needed for your service

    public rotateClockWise(clockwise: boolean) {
        if (clockwise) {
            this.rotateWithDegree(this.currentRotateDeg + 90);
        } else {
            this.rotateWithDegree(this.currentRotateDeg - 90);
        }
    }

    public rotateWithDegree(degree: number) {
        if (this.rotate(degree) && this.applyTransform()) {
            RcContext.instance.session.sendMessage(new VideoRotationMessage(this.currentRotateDeg));
        }
    }

    private rotate(degree: number) {
        if (degree && this.isCanvasVisible() && this.previousRotateDeg != degree) {
            this.previousRotateDeg = this.currentRotateDeg;
            this.currentRotateDeg = degree;
            return true;
        }
        return false;
    }

    public getPreviousRotateDeg(): number {
        return this.previousRotateDeg;
    }

    public resetScale() {
        const canvasOuter = document.getElementById('canvasOuter');
        if (this.isCanvasVisible() && !this.transformAnimationService.isInTransition()) {
            this.transformAnimationService.clearTransition();
            this.currentZoomScale = 1.0;
            (RcContext.instance.deviceContext as DeviceContext).scaleFactor = this.currentZoomScale;
            // canvasOuter.style.transform = ("rotate(" + this.currentRotateDeg + "deg ) scale(" + 1.0 + ")");
            canvasOuter.style.transform = ("scale(" + 1.0 + ")");
        }
    }

    public translate(x?: number, y?: number) {
        const canvasOuter = document.getElementById('canvasOuter');
        if (this.isCanvasVisible()) {
            canvasOuter.style.translate = (x ?? 0) +"px " + (y ?? 0) + "px";
        }
    }

    public isOuterCanvasResized(): boolean {
        const canvasOuter = document.getElementById('canvasOuter');
        if (canvasOuter.clientHeight !== this.outerCanvasHeight || canvasOuter.clientWidth !== this.outerCanvasWidth) {
            this.outerCanvasHeight = canvasOuter.clientHeight;
            this.outerCanvasWidth = canvasOuter.clientHeight;
            return true;
        }
        return false;
    }

    private applyTransform(needTransition?: boolean): boolean {
        const canvasOuter = document.getElementById('canvasOuter');
        if (this.isCanvasVisible()) {
            const deviceContext = RcContext.instance.deviceContext as DeviceContext;
            // visible canvas
            // NOTE: add transition here
            // NOTE: Edge case when scale less than 25 or greater than 100
            // NOTE 2024.0.4: current do not need animation
            if (needTransition) {
                this.startTransition();
            } else {
                this.transformAnimationService.clearTransition();
            }
            deviceContext.scaleFactor = this.currentZoomScale;
            if (this.currentZoomScale <= 1) {
                deviceContext.targetX = 0;
                deviceContext.targetY = 0;
            }
            // canvasOuter.style.transform = ("rotate(" + this.currentRotateDeg + "deg ) scale(" + this.currentZoomScale + ")");
            canvasOuter.style.transform = ("scale(" + this.currentZoomScale + ")");
            return true;
        }

        return false;
    }

    private startTransition() {
        if (this.timer) {
            clearTimeout(this.timer)
        }
        this.transformAnimationService.applyTransition('transform');
        this.timer = setTimeout(() => {
            this.transformAnimationService.completeTransition();
        }, this.transformAnimationService.getTransitionDuration() + 10);
    }

    private isCanvasVisible(): boolean {
        const canvasOuter = document.getElementById("canvasOuter");
        return canvasOuter && canvasOuter.clientWidth !== 0 && canvasOuter.clientHeight !== 0;
    }
}
